import { Fragment, useState } from 'react'

import { Anchor, Button, Group, Loader, Space, Text } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { showAlert } from '#components/Modals'
import {
  LABELED_TIERS,
  STATUS_LABELS,
  STRIPE_TIER,
} from '#components/Settings/Org/plans'
import { useStaffWorkspaceBillingPlan } from '#components/Settings/Org/useBillingPlan'
import { useMst } from '#state'
import { extractTsRestSuccess } from '#util/extractTsRest'
import {
  SyncBillingSeatsPayload,
  WorkspaceDetailsPayload,
  WorkspaceTier,
} from '#util/ScriptoApiClient/types'

import { stripeCustomerUrl } from '../helpers'

type StaffBillingPlanQuery = ReturnType<typeof useStaffWorkspaceBillingPlan>

function TierRadio({
  orgId,
  tier,
  isCurrent,
  onChange,
}: {
  orgId: string
  tier: string[]
  isCurrent: boolean
  onChange: (tier: WorkspaceTier) => void
}) {
  const { scrapi } = useMst()
  const tierCode = tier[0]
  const tierLabel = tier[1]

  const changeTier = async (tier: WorkspaceTier) => {
    try {
      await extractTsRestSuccess(
        scrapi.staff.updateWorkspace({
          params: { id: orgId },
          body: { tier },
        }),
        200,
      )

      if (tier === STRIPE_TIER) {
        await extractTsRestSuccess(
          scrapi.staff.workspaceBillingSync({
            params: { id: orgId },
            body: {},
          }),
          200,
        )
      }

      onChange(tier)
      showAlert({
        title: 'Success',
        children:
          tier === STRIPE_TIER
            ? 'Workspace tier updated and entitlements synced'
            : 'Workspace tier updated successfully',
      })
    } catch (e) {
      showAlert({
        title: 'Error',
        // oxlint-disable-next-line typescript/no-explicit-any
        children: (e as any).message,
      })
    }
  }

  return (
    <>
      <input
        className="s-input s-input--radio s-input--inline"
        type="radio"
        id={tierCode}
        value={tierCode}
        name="tier"
        onChange={() => changeTier(tierCode as WorkspaceTier)}
        checked={isCurrent}
      />
      <label
        className="s-label s-label--radio s-label--inline"
        htmlFor={tierCode}
      >
        {tierLabel}
      </label>
    </>
  )
}

const SKIPPED_MESSAGES: Record<
  Extract<SyncBillingSeatsPayload, { status: 'skipped' }>['reason'],
  string
> = {
  'billing-disabled': 'Billing is turned off in this environment',
  'non-stripe-workspace': 'This workspace is not on the Stripe tier',
  'no-stripe-customer': 'The workspace owner has no Stripe customer',
  'no-subscription': 'The Stripe customer has no subscription',
  'subscription-not-billable':
    'The subscription is cancelled or unpaid, so the seat count stays where it is',
}

const syncResultMessage = (result: SyncBillingSeatsPayload) => {
  if (result.status === 'skipped') return SKIPPED_MESSAGES[result.reason]

  const seats = `${result.quantity} seat${result.quantity === 1 ? '' : 's'}`
  return result.status === 'synced'
    ? `Stripe is now billing ${seats}`
    : `Stripe was already billing ${seats}`
}

const SyncSeats = ({ orgId }: { orgId: string }) => {
  const { apiClient } = useMst()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    async mutationFn() {
      return apiClient.syncBillingSeatsByStaff({ orgId })
    },
    async onSuccess(result) {
      await queryClient.invalidateQueries({
        queryKey: ['staff.workspaceBillingPlan', orgId],
      })
      showAlert({
        title: result.status === 'skipped' ? 'Nothing to sync' : 'Success',
        children: syncResultMessage(result),
      })
    },
    onError(e) {
      showAlert({
        title: 'Error',
        // oxlint-disable-next-line typescript/no-explicit-any
        children: (e as any).message,
      })
    },
  })

  return (
    <Button
      size="xs"
      variant="default"
      loading={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      Sync seats
    </Button>
  )
}

function StripePlanSummary({
  orgId,
  planQuery,
}: {
  orgId: string
  planQuery: StaffBillingPlanQuery
}) {
  if (planQuery.isPending) return <Loader size="sm" />

  if (!planQuery.isSuccess) {
    return (
      <Text c="dimmed">Couldn&rsquo;t load billing for this workspace</Text>
    )
  }

  if (!planQuery.data) return <Text>No subscription</Text>

  const { planName, status, seats, interval } = planQuery.data
  const billed = interval === 'year' ? 'billed yearly' : 'billed monthly'

  return (
    <Group gap="sm">
      <Text>
        {planName ?? 'Unnamed plan'} ({STATUS_LABELS[status]})
        {seats ? `, ${seats} seats, ${billed}` : ''}
      </Text>
      <SyncSeats orgId={orgId} />
    </Group>
  )
}

export function TierForm({ org }: { org: WorkspaceDetailsPayload }) {
  const [tier, setTier] = useState(org.tier)
  const { environment } = useMst()
  const planQuery = useStaffWorkspaceBillingPlan(org.id, {
    enabled: tier === STRIPE_TIER,
  })
  const customerId = planQuery.data?.stripeCustomerId ?? null

  return (
    <>
      <h3>Tier</h3>
      <Text>
        controls what workspace features are available to the client and how
        many users they can invite
      </Text>
      <Space h={10} />
      <form className="s-form">
        <fieldset className="s-form__fieldset">
          {LABELED_TIERS.map((t) => (
            <Fragment key={t[0]}>
              <TierRadio
                tier={t}
                isCurrent={t[0] === tier}
                orgId={org.id}
                onChange={setTier}
              />
            </Fragment>
          ))}
        </fieldset>
      </form>
      {tier === STRIPE_TIER ? (
        <>
          <Space h={10} />
          <StripePlanSummary orgId={org.id} planQuery={planQuery} />
          <Space h={10} />
          {customerId ? (
            <Anchor
              href={stripeCustomerUrl(environment.config, customerId)}
              target="_blank"
              rel="noreferrer"
            >
              Open customer in Stripe
            </Anchor>
          ) : null}
        </>
      ) : null}
    </>
  )
}
